// client/src/v2/components/map/FitCatchmentBounds.jsx

import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import * as turf from "@turf/turf";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { filterDefinitionsToLatestYear } from "../../utils/catchmentYearUtils";

/**
 * Soft ceiling for normal catchments — one fitBounds only (no post-fit snap).
 * Large policy radii (≥ 10 mi) keep Leaflet’s natural fit so circles are not cropped (rule F).
 */
const NORMAL_CATCHMENT_MAX_ZOOM = 13;

/**
 * Large policy radii (e.g. CCHSG 25 mi) need a lower zoom than a “readable” street-level fit.
 * Do not force a higher zoom after fitBounds for these — that crops the circle.
 */
const LARGE_RADIUS_METERS = 10 * 1609.344; // 10 miles

function radiusToMeters(radius, unit) {
  const r = Number(radius);
  if (!Number.isFinite(r) || r <= 0) return null;
  const u = String(unit || "").toLowerCase().trim();
  if (u === "km" || u === "kilometer" || u === "kilometre" || u === "kilometers" || u === "kilometres")
    return r * 1000;
  if (u === "m" || u === "meter" || u === "metre" || u === "meters" || u === "metres") return r;
  if (u === "mi" || u === "mile" || u === "miles") return r * 1609.344;
  return r * 1000;
}

/** True when latest-year definitions include a radius large enough that a high maxZoom would crop it. */
function hasLargeRadiusBand(payload) {
  const definitions = filterDefinitionsToLatestYear(payload?.definitions || []);
  for (const def of definitions) {
    if (String(def.geography_type || "").toLowerCase() !== "radius") continue;
    const meters = radiusToMeters(def.radius, def.radius_unit);
    if (meters != null && meters >= LARGE_RADIUS_METERS) return true;
  }
  return false;
}

export function getBoundsFromPayload(payload) {
  if (!payload?.school) return null;

  const school = payload.school;
  const schoolLat = school?.lat != null ? Number(school.lat) : null;
  const schoolLon = school?.lon != null ? Number(school.lon) : null;
  const schoolCenter =
    Number.isFinite(schoolLat) && Number.isFinite(schoolLon) ? [schoolLat, schoolLon] : null;

  const definitions = filterDefinitionsToLatestYear(payload.definitions || []);
  const geometriesByKey = payload.geometriesByKey || {};

  const allBounds = [];

  for (const def of definitions) {
    const geographyType = String(def.geography_type || "").toLowerCase();

    if (geographyType === "radius") {
      const meters = radiusToMeters(def.radius, def.radius_unit);
      const style = def?.style && typeof def.style === "object" ? def.style : {};
      const centerLat = Number(style.center_lat);
      const centerLon = Number(style.center_lon);
      const circleCenter =
        Number.isFinite(centerLat) && Number.isFinite(centerLon)
          ? [centerLat, centerLon]
          : schoolCenter;
      if (circleCenter && meters) {
        const [lat, lon] = circleCenter;
        const circle = turf.circle([lon, lat], meters / 1000, { units: "kilometers" });
        const [minX, minY, maxX, maxY] = turf.bbox(circle);
        allBounds.push(L.latLngBounds([minY, minX], [maxY, maxX]));
      }
    } else {
      const bucket = geometriesByKey?.[def.catchment_key]?.geometries || [];
      for (const g of bucket) {
        const geojson = g?.geojson;
        if (!geojson) continue;
        try {
          const [minX, minY, maxX, maxY] = turf.bbox(geojson);
          allBounds.push(L.latLngBounds([minY, minX], [maxY, maxX]));
        } catch {
          // skip invalid geometry
        }
      }
    }
  }

  if (allBounds.length === 0) return null;
  if (allBounds.length === 1) return allBounds[0];
  return allBounds.reduce((acc, b) => acc.extend(b));
}

/** True when the catchment already sits inside the current map view — no camera move. */
function catchmentAlreadyInView(map, bounds) {
  try {
    const view = map.getBounds();
    if (!view || !bounds?.isValid?.()) return false;
    return view.contains(bounds);
  } catch {
    return false;
  }
}

/**
 * Pans the map to fit the catchment area when a catchment checkbox is selected.
 * Only pans on select (add), not on deselect.
 * One camera move only — no post-fit zoom snap. Skips if already fully in view.
 *
 * Phone: PhoneMapViewportClamp patches getSize() so Leaflet frames within the visible strip.
 */
export default function FitCatchmentBounds({ selectedIds, catchmentsBySchoolId }) {
  const map = useMap();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const prevSelectedRef = useRef([]);
  const pendingPanRef = useRef(null);

  useEffect(() => {
    const selected = Array.isArray(selectedIds) ? selectedIds : [];
    const pending = pendingPanRef.current;
    if (pending != null && !selected.includes(pending)) {
      pendingPanRef.current = null;
    }

    const prev = prevSelectedRef.current;
    const newlyAdded = selected.filter((id) => !prev.includes(id));
    prevSelectedRef.current = selected;

    if (newlyAdded.length > 0) {
      pendingPanRef.current = newlyAdded[newlyAdded.length - 1];
    }
  }, [selectedIds]);

  useEffect(() => {
    const targetId = pendingPanRef.current;
    if (targetId == null) return;

    const selected = Array.isArray(selectedIds) ? selectedIds : [];
    if (!selected.includes(targetId)) {
      pendingPanRef.current = null;
      return;
    }

    const payload = catchmentsBySchoolId?.[targetId];
    if (payload == null) return;

    if (!payload.definitions?.length) {
      pendingPanRef.current = null;
      return;
    }

    const bounds = getBoundsFromPayload(payload);
    if (!bounds) {
      pendingPanRef.current = null;
      return;
    }

    pendingPanRef.current = null;

    // Stacking nearby schools (e.g. Sutton + Wallington Boys): keep the camera still.
    if (catchmentAlreadyInView(map, bounds)) {
      return;
    }

    const largeRadius = hasLargeRadiusBand(payload);
    map.fitBounds(bounds, {
      padding: isPhone ? [20, 20] : [60, 60],
      animate: true,
      // Soft ceiling for normal catchments; large radii use natural fit (rule F).
      maxZoom: largeRadius ? 16 : NORMAL_CATCHMENT_MAX_ZOOM,
    });
  }, [selectedIds, catchmentsBySchoolId, map, isPhone]);

  return null;
}
